import { HEIC_PREVIEW_JPEG_QUALITY } from '../constants/media';
import { reportError } from '../sentry';
import { isHeicFile, isHeicUrl } from './function';
import { resolveIsHeicFile } from './heicDetection';

const heicBlobCache = new Map();
const previewUrlCache = new Map();

let heicToModuleLoader = null;

/** CSP-safe HEIC decoder (works without unsafe-eval in production). */
const loadHeicTo = () => {
  if (!heicToModuleLoader) {
    heicToModuleLoader = import('heic-to/csp');
  }
  return heicToModuleLoader;
};

/**
 * Stable identity key for a local File/Blob (used for caching and comparisons).
 * @param {File|Blob} file
 * @returns {string}
 */
export const getFileIdentityKey = (file) =>
  file ? `${file.name}-${file.size}-${file.lastModified}` : '';

const normalizeHeicFile = (file) => {
  if (!file) return file;

  // Always re-wrap with image/heic — this helper is only called for confirmed HEIC content.
  // Android often mislabels HEIC as image/jpeg, which breaks the decoder without this.
  return new File([file], file.name, {
    type: 'image/heic',
    lastModified: file.lastModified,
  });
};

const convertHeicToJpegBlob = async (file) => {
  const { heicTo } = await loadHeicTo();
  const input = normalizeHeicFile(file);

  return heicTo({
    blob: input,
    type: 'image/jpeg',
    quality: HEIC_PREVIEW_JPEG_QUALITY,
  });
};

const getHeicPreviewBlob = async (file) => {
  const key = getFileIdentityKey(file);
  if (heicBlobCache.has(key)) {
    return heicBlobCache.get(key);
  }

  const promise = convertHeicToJpegBlob(file)
    .then((blob) => (blob instanceof Blob && blob.size > 0 ? blob : null))
    .catch((error) => {
      heicBlobCache.delete(key);
      reportError(error, 'HEIC preview conversion');
      return null;
    });

  heicBlobCache.set(key, promise);
  return promise;
};

/**
 * Creates a browser-displayable preview URL for a local file.
 * HEIC/HEIF files are converted to JPEG since most browsers cannot render them in <img>.
 * URLs are cached per file identity and must be released via revokeMediaPreviewUrl.
 * @param {File} file
 * @returns {Promise<string|null>}
 */
export const createMediaPreviewUrl = async (file) => {
  if (!file) return null;

  const key = getFileIdentityKey(file);
  if (previewUrlCache.has(key)) {
    return previewUrlCache.get(key);
  }

  let url = null;
  const treatAsHeic = await resolveIsHeicFile(file, isHeicFile);

  if (treatAsHeic) {
    const previewBlob = await getHeicPreviewBlob(file);
    if (previewBlob) {
      url = URL.createObjectURL(previewBlob);
    }
  } else {
    try {
      url = URL.createObjectURL(file);
    } catch {
      url = null;
    }
  }

  if (url) {
    previewUrlCache.set(key, url);
  }

  return url;
};

/**
 * Resolves a remote image URL to one the browser can display.
 * HEIC/HEIF URLs are fetched and converted to a cached JPEG blob URL.
 * @param {string} url
 * @returns {Promise<string>}
 */
export const resolveDisplayImageUrl = async (url) => {
  if (!url || !isHeicUrl(url)) {
    return url;
  }

  const cacheKey = `url:${url}`;
  if (previewUrlCache.has(cacheKey)) {
    return previewUrlCache.get(cacheKey);
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return url;
    }

    const blob = await response.blob();
    const file = new File([blob], 'image.heic', {
      type: blob.type || 'image/heic',
      lastModified: Date.now(),
    });
    const jpegBlob = await getHeicPreviewBlob(file);

    if (!jpegBlob) {
      return url;
    }

    const objectUrl = URL.createObjectURL(jpegBlob);
    previewUrlCache.set(cacheKey, objectUrl);
    return objectUrl;
  } catch (error) {
    reportError(error, 'remote HEIC preview');
    return url;
  }
};

/**
 * Ensures a file object has a displayable previewUrl.
 * Skips HEIC — previews are generated on demand by FilePreviewMedia / useMediaPreview.
 * @param {{ file: File, previewUrl?: string, caption?: string }} fileObj
 * @returns {Promise<{ file: File, previewUrl: string|null, caption: string }>}
 */
export const ensureFilePreview = async (fileObj) => {
  if (!fileObj || fileObj.previewUrl) {
    return fileObj;
  }

  const treatAsHeic = await resolveIsHeicFile(fileObj.file, isHeicFile);
  if (treatAsHeic) {
    return {
      ...fileObj,
      caption: fileObj.caption ?? '',
      previewUrl: null,
    };
  }

  const previewUrl = await createMediaPreviewUrl(fileObj.file);
  return {
    ...fileObj,
    caption: fileObj.caption ?? '',
    previewUrl,
  };
};

/**
 * Prepares one or more upload file objects for the upload UI.
 * @param {Array<{ file: File, caption?: string, previewUrl?: string }>} fileObjs
 * @returns {Promise<Array>}
 */
export const prepareUploadFileObjs = (fileObjs) =>
  Promise.all(fileObjs.map((fileObj) => ensureFilePreview(fileObj)));

/**
 * Revokes a preview blob URL created by createMediaPreviewUrl.
 * @param {{ file?: File, previewUrl?: string }} fileObj
 */
export const revokeMediaPreviewUrl = (fileObj) => {
  if (!fileObj?.file) return;

  const key = getFileIdentityKey(fileObj.file);
  const url = fileObj.previewUrl || previewUrlCache.get(key);

  if (url) {
    URL.revokeObjectURL(url);
    previewUrlCache.delete(key);
    heicBlobCache.delete(key);
  }
};

/** Warm-load the HEIC decoder when the upload UI opens. */
export const preloadHeicDecoder = () => {
  loadHeicTo().catch(() => {});
};
